import React from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getTypeList,
  getComponentList,
  getGridTypeList,
  getRequiredList,
  getFieldList,
  getDisabledList,
} from "../../selectors";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "0px 10px" };

const CreateEditDialog = (props) => {

  const {
    open,
    tableCode,
    codver,
    isLoading,
    data,
    rows,
    handleClose,
    create,
    modify,
  } = props;

  const typeList = useSelector(getTypeList);
  const componentList = useSelector(getComponentList);
  const gridTypeList = useSelector(getGridTypeList);
  const requiredList = useSelector(getRequiredList);
  const fieldList = useSelector(getFieldList);
  const disabledList = useSelector(getDisabledList);

  const dispatch = useDispatch();

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, tableCode, codver, fieldList, create, modify, openValidationError);

  const codesInUse = rows.map((item) => item.code);
  const codeOptionList = fieldList.filter((item) => !codesInUse.includes(item.key));
  const filterFieldOptionList = rows.filter((item) => item.component === "L").map((item) => ({ key: item.code, value: item.name }));

  const fieldHasRelatedTable = Boolean(fieldList.find(item => item.key === formData.code)?.hasRelatedTable);
  const fieldHasRelatedOptionList = Boolean(fieldList.find(item => item.key === formData.code)?.hasRelatedOptionList);

  if (isLoading) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {isCreate ? getLocalizedString("createFieldDefinition") : getLocalizedString("editFieldDefinition")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent style={{ paddingTop: 0 }}>
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-code`}>{getLocalizedString("fieldCode")}</InputLabel>
                <Select
                  disabled={!isCreate}
                  value={formData.code}
                  labelId={`label-code`}
                  id={`select-code`}
                  onChange={(e) => setFormValue(e.target.value, "code")}
                  margin={"none"}
                >
                  {
                    (isCreate ? codeOptionList : fieldList) // si no es create va la lista sin filtrar para que salga la opción ya seteada
                      ?.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {`${item.value} (${item.key})`}
                        </MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                fullWidth
                margin={"none"}
                label={getLocalizedString("fieldName")}
                onChange={(e) => setFormValue(e.target.value, "name")}
                value={formData.name}
                inputProps={{ maxLength: 30 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-type`}>{getLocalizedString("fieldType")}</InputLabel>
                <Select
                  value={formData.type}
                  labelId={`label-type`}
                  id={`select-type`}
                  onChange={(e) => setFormValue(e.target.value, "type")}
                  margin={"none"}
                >
                  {
                    typeList?.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-required`}>{getLocalizedString("entryType")}</InputLabel>
                <Select
                  value={formData.required}
                  labelId={`label-required`}
                  id={`select-required`}
                  onChange={(e) => setFormValue(e.target.value, "required")}
                  margin={"none"}
                >
                  {
                    requiredList?.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                style={{ marginTop: 14 }}
                fullWidth
                margin={"none"}
                label={getLocalizedString("fieldComment")}
                onChange={(e) => setFormValue(e.target.value, "comment")}
                value={formData.comment}
                inputProps={{ maxLength: 40 }}
                helperText={getLocalizedString("max40Char", { length: formData.comment.length })}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                InputProps={{ inputComponent: CustomNumberFormat }}
                fullWidth
                margin={"none"}
                label={getLocalizedString("order")}
                onChange={(e) => setFormValue(e.target.value, "order")}
                value={formData.order}
                inputProps={{ min: "1", step: "1", max: 99, decimals: 0 , isAllowed: (v) => v.floatValue === undefined || v.floatValue <= 99}}
                onInput={e => {
                  setFormValue(e.target.value, 'order');
                }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-component`}>{getLocalizedString("visualComponentForEditor")}</InputLabel>
                <Select
                  value={formData.component}
                  labelId={`label-component`}
                  id={`select-component`}
                  onChange={(e) => setFormValue(e.target.value, "component")}
                  margin={"none"}
                >
                  {
                    (componentList[formData.type] || []).map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-hidden`}>{getLocalizedString("hidden")}</InputLabel>
                <Select
                  value={formData.hidden}
                  labelId={`label-hidden`}
                  id={`select-hidden`}
                  onChange={(e) => setFormValue(e.target.value, "hidden")}
                  margin={"none"}
                >
                  {
                    [{ key: 'N', value: getLocalizedString("no") }, { key: 'Y', value: getLocalizedString("yes") }].map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-keyField`}>{getLocalizedString("repetitionControl")}</InputLabel>
                <Select
                  value={formData.keyField}
                  labelId={`label-keyField`}
                  id={`select-keyField`}
                  onChange={(e) => setFormValue(e.target.value, "keyField")}
                  margin={"none"}
                >
                  {
                    [{ key: 'N', value: getLocalizedString("no") }, { key: 'Y', value: getLocalizedString("yes") }].map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-disabled`}>{getLocalizedString("protected")}</InputLabel>
                <Select
                  value={formData.disabled}
                  labelId={`label-disabled`}
                  id={`select-disabled`}
                  onChange={(e) => setFormValue(e.target.value, "disabled")}
                  margin={"none"}
                >
                  {
                    disabledList.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-columnTypeInGrid`}>{getLocalizedString("gridColumnType")}</InputLabel>
                <Select
                  value={formData.columnTypeInGrid}
                  labelId={`label-columnTypeInGrid`}
                  id={`select-columnTypeInGrid`}
                  onChange={(e) => setFormValue(e.target.value, "columnTypeInGrid")}
                  margin={"none"}
                >
                  {
                    gridTypeList?.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            {
              ["L", "LM"].includes(formData.component) &&
              <Grid container item alignItems="center" xs={12} style={{ padding: "20px 10px" }}>
                {
                  fieldHasRelatedTable &&
                  <Typography className="whiteText" style={{ margin: 10, marginBottom: 20, marginTop: 0 }}>
                    {getLocalizedString("thisFieldHasAnAutomaticTableRelationshipThereforeTheDropdownListWillLoadAutomaticallyUnlessYouEnterYourOwnSqlForTheList")}
                  </Typography>
                }
                {
                  fieldHasRelatedOptionList &&
                  <Typography className="whiteText" style={{ margin: 10, marginBottom: 20, marginTop: 0 }}>
                    {getLocalizedString("fieldHasValidValuesList")}
                  </Typography>
                }
                <TextFieldMultiline
                  required={!fieldHasRelatedTable && !fieldHasRelatedOptionList}
                  fullWidth
                  label={getLocalizedString("sqlForDropdownList")}
                  rows={10}
                  margin={"none"}
                  value={formData.sql}
                  onChange={(e) => setFormValue(e.target.value, "sql")}
                  inputProps={{ spellCheck: 'false' }}
                  helperText={
                    <>
                      {getLocalizedString("dropdownSQLWarning1")}
                      <br />
                      {getLocalizedString("dropdownSQLWarning2")}
                      <br />
                      {getLocalizedString("sqlVariablesAvailableHelperText")}
                    </>
                  }
                />
              </Grid>
            }
            {
              formData.type === 'A' &&
              <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                <TextField
                  required
                  InputProps={{ inputComponent: CustomNumberFormat }}
                  fullWidth
                  margin={"none"}
                  label={getLocalizedString("size")}
                  onChange={(e) => setFormValue(e.target.value, "size")}
                  value={formData.size}
                  inputProps={{ min: "1", step: "1", decimals: 0 }}
                  onInput={e => {
                    setFormValue(e.target.value, 'size');
                  }}
                />
              </Grid>
            }
            {
              formData.component === 'L' &&
              <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel shrink id={`label-filterField`}>{getLocalizedString("filterField")}</InputLabel>
                  <Select
                    value={formData.filterField}
                    labelId={`label-filterField`}
                    id={`select-kfilterFieldeyField`}
                    onChange={(e) => setFormValue(e.target.value, "filterField")}
                    margin={"none"}
                    displayEmpty
                  >
                    {
                      [
                        { key: "", value: "Ninguno" },
                        ...(filterFieldOptionList || [])
                      ].map((item) => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            }
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextField
                style={{ marginTop: 14 }}
                fullWidth
                margin={"none"}
                label={getLocalizedString("tooltip")}
                onChange={(e) => setFormValue(e.target.value, "tooltip")}
                value={formData.tooltip}
                inputProps={{ maxLength: 254 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} style={{ padding: 10 }}>
              <TextFieldMultiline
                fullWidth
                label={getLocalizedString("alternativeFieldName")}
                rows={5}
                margin={"none"}
                value={formData.sqlLang}
                onChange={(e) => setFormValue(e.target.value, "sqlLang")}
                inputProps={{ spellCheck: 'false' }}
                helperText={ //Puede usar '#LANGUAGE#' y debe usar AS "Title" para el titulo personalizado por sql
                  getLocalizedString("alternativeFieldNameHelperText")
                }
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} style={{ padding: 10 }}>
              <TextFieldMultiline
                fullWidth
                label={getLocalizedString("protectionSql")}
                rows={10}
                margin={"none"}
                value={formData.protectionSql}
                onChange={(e) => setFormValue(e.target.value, "protectionSql")}
                inputProps={{ spellCheck: 'false' }}
                helperText={
                  <>
                    {getLocalizedString("protectionSqlHelperText")}
                    <br />
                    {getLocalizedString("sqlVariablesAvailableHelperText")}
                  </>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default CreateEditDialog;